import * as React from 'react';
import { deburr } from 'es-toolkit';
import { cn } from '../../utils';
import { Input } from '../inputs/Input';
import { Icon } from '../icons/Icon';
import { Loader } from '../loaders';
import { FilterChoiceRow } from './FilterChoiceRow';

export interface FilterOptionListOption {
  value: string;
  label: string;
  /** Secondary text on the right, e.g. a code */
  description?: string;
}

export interface FilterOptionListProps {
  options: FilterOptionListOption[];
  /** Selected values */
  value: string[];
  onChange: (next: string[]) => void;
  /** Checkboxes when true, one choice when false. @default true */
  multiple?: boolean;
  /** Shows a search input above the options */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Remote search; without it the options are filtered locally */
  onSearch?: (query: string) => void;
  /** Shows a loader instead of the options */
  loading?: boolean;
  /** Shown when no option matches */
  emptyLabel: string;
  className?: string;
  testId: string;
}

const normalize = (text: string): string => deburr(text).toLowerCase();

/** Selectable values of a filter: checkbox rows, or one-choice rows with `multiple={false}`. */
export const FilterOptionList = ({
  options,
  value,
  onChange,
  multiple = true,
  searchable,
  searchPlaceholder,
  onSearch,
  loading,
  emptyLabel,
  className,
  testId
}: FilterOptionListProps) => {
  const [query, setQuery] = React.useState('');
  const groupName = React.useId();

  const visible = React.useMemo(() => {
    if (onSearch || query.trim() === '') return options;
    const needle = normalize(query.trim());
    return options.filter(option => normalize(option.label).includes(needle));
  }, [options, query, onSearch]);

  const toggle = (optionValue: string) => {
    if (!multiple) {
      onChange([optionValue]);
      return;
    }
    onChange(
      value.includes(optionValue) ? value.filter(v => v !== optionValue) : [...value, optionValue]
    );
  };

  return (
    <div data-testid={testId} className={cn('flex flex-col gap-xs', className)}>
      {searchable && (
        <Input
          height='sm'
          value={query}
          placeholder={searchPlaceholder}
          testId={`${testId}-search`}
          leftContent={
            <Icon name='magnifying-glass' className='w-3.5! h-3.5!' aria-hidden='true' />
          }
          onChange={e => {
            setQuery(e.target.value);
            onSearch?.(e.target.value);
          }}
        />
      )}
      <div className='max-h-60 overflow-y-auto'>
        {loading ? (
          <div className='flex justify-center py-sm' data-testid={`${testId}-loading`}>
            <Loader size='sm' />
          </div>
        ) : visible.length === 0 ? (
          <p className='px-sm py-sm text-xs text-foreground-muted'>{emptyLabel}</p>
        ) : (
          visible.map(option => (
            <FilterChoiceRow
              key={option.value}
              type={multiple ? 'checkbox' : 'radio'}
              name={groupName}
              label={option.label}
              description={option.description}
              checked={value.includes(option.value)}
              onChange={() => toggle(option.value)}
              testId={`${testId}-option-${option.value}`}
            />
          ))
        )}
      </div>
    </div>
  );
};

FilterOptionList.displayName = 'FilterOptionList';
